"use strict";

// ─── PeerSync: Real-time P2P score sync between Scorer ↔ Recorder phones ──
// Uses PeerJS (WebRTC DataChannel) for sub-10ms latency on same WiFi.
// Internet needed only for initial handshake (2-3s), then all data is P2P.

import { get } from 'svelte/store';
import { activeMatch, currentScreen, fullScreenBoard, peerRole, peerConnected, peerRoomCode, peerStatus } from './svelte/store.js';
import { validateMatchSnapshot } from './match-schema.js';
import { isNewerPeerSnapshot } from './sync/snapshot-order.js';
import { initSupabaseFallbackChannel, closeSupabaseFallbackChannel } from './sync/supabase-fallback.js';
// Animation, sound and recording commands are exposed through callbacks so the
// transport stays independent from the Svelte media runtime.

if (typeof window !== "undefined") {
  window.addEventListener('beforeunload', () => {
    if (PeerSync) PeerSync.disconnect();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && PeerSync) PeerSync.disconnect();
  });
}

let _peer = null;
let _conn = null;
let _heartbeatId = null;
let _reconnectTimer = null;
let _reconnectAttempts = 0;
let _connectionGeneration = 0;
let _lastReceivedSnapshot = null;
const MAX_RECONNECT = 10;
const HEARTBEAT_MS = 3000;
const PEER_PREFIX = "ffpl_cs_";

// Recording commands are NOT idempotent and must not be lost: a dropped "start"
// leaves the scorer showing RECORDING while the camera phone sits in STANDBY.
// Each command carries a sequence number and is retried until acknowledged.
const RECCMD_RETRY_MS = 1000;
const RECCMD_MAX_ATTEMPTS = 15;
let _recCmdSeq = 0;
let _monotonicFallback = 0;
const _pendingRecCmds = new Map();
let _recCmdRetryTimer = null;
let _onRecCmdFailed = null;

function _stopRecCmdRetries() {
  if (_recCmdRetryTimer) { clearInterval(_recCmdRetryTimer); _recCmdRetryTimer = null; }
  _pendingRecCmds.clear();
}

function _ensureRecCmdRetryLoop() {
  if (_recCmdRetryTimer) return;
  _recCmdRetryTimer = setInterval(() => {
    if (_pendingRecCmds.size === 0) {
      clearInterval(_recCmdRetryTimer);
      _recCmdRetryTimer = null;
      return;
    }
    if (!_conn || !_conn.open) return;
    for (const [seq, entry] of _pendingRecCmds) {
      entry.attempts++;
      if (entry.attempts > RECCMD_MAX_ATTEMPTS) {
        _pendingRecCmds.delete(seq);
        console.warn(`[PeerSync] Recording command "${entry.msg.d.action}" was never acknowledged.`);
        if (_onRecCmdFailed) _onRecCmdFailed(entry.msg.d.action);
        continue;
      }
      _send(entry.msg);
    }
  }, RECCMD_RETRY_MS);
}

// Event callbacks for things that aren't stores (anim, sfx, recCmd)
let _onState = null;
let _onAnim = null;
let _onSfx = null;
let _onRecCmd = null;
let _onRecStatus = null;
let _onAudioStream = null;
let _onConnected = null;
let _onDisconnected = null;

// STUN-only ICE: both phones are expected to be on the same WiFi/hotspot, so
// direct connections work without a relay. (The old openrelay.metered.ca free
// TURN service shut down — its dead entries only slowed down connections.
// If cross-network sync is ever needed, add a paid/owned TURN server here.)
const ICE_CONFIG = {
  iceTransportPolicy: 'all',
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "stun:stun2.l.google.com:19302" },
    { urls: "stun:global.stun.twilio.com:3478" }
  ]
};

function _genCode() {
  return String(Math.floor(100000 + Math.random() * 900000));
}

function _peerId(code) {
  return PEER_PREFIX + code;
}

function _fireStatus(statusStr, detail = "") {
  peerStatus.set(statusStr);
  if (detail) console.log(`[PeerSync] ${statusStr}: ${detail}`);
}

function _isPeerJSLoaded() {
  return typeof window.Peer !== "undefined";
}

function _ensurePeerJS() {
  return new Promise((resolve, reject) => {
    if (_isPeerJSLoaded()) return resolve();
    const script = document.createElement("script");
    script.src = "/peerjs.min.js";
    script.onload = () => {
      if (_isPeerJSLoaded()) resolve();
      else reject(new Error("PeerJS failed to initialize"));
    };
    script.onerror = () => reject(new Error("Failed to load PeerJS library. Check internet."));
    document.head.appendChild(script);
  });
}

function _startHeartbeat() {
  _stopHeartbeat();
  _heartbeatId = setInterval(() => {
    if (_conn && _conn.open) {
      try { _conn.send(JSON.stringify({ v: 1, t: "ping", ts: Date.now() })); } catch (_) {}
    }
  }, HEARTBEAT_MS);
}

function _stopHeartbeat() {
  if (_heartbeatId) { clearInterval(_heartbeatId); _heartbeatId = null; }
}

function _setupConnection(conn) {
  const generation = ++_connectionGeneration;
  const previous = _conn;
  _conn = conn;
  if (previous && previous !== conn) {
    try { previous.close(); } catch (_) {}
  }

  // PeerJS does not reliably fire `close` on the DataChannel when ICE fails, so
  // the connection can sit in a permanent zombie state. Watch the underlying
  // RTCPeerConnection and force a close so the reconnect path can take over.
  if (conn.peerConnection) {
    try {
      conn.peerConnection.addEventListener('iceconnectionstatechange', () => {
        const state = conn.peerConnection.iceConnectionState;
        if (state === "failed") {
          console.warn("[PeerSync] ICE state failed — closing to trigger reconnect");
          try { conn.close(); } catch (_) {}
        }
      });
    } catch (_) {}
  }

  conn.on("open", () => {
    if (_conn !== conn || generation !== _connectionGeneration) return;
    _reconnectAttempts = 0;
    console.log("[PeerSync] DataChannel open with:", conn.peer);
    _fireStatus("connected", conn.peer);
    peerConnected.set(true);
    _startHeartbeat();
    if (get(peerRole) === "scorer") {
      const match = get(activeMatch);
      const snapshot = PeerSync.buildStateSnapshot(match, {
        board: get(fullScreenBoard),
        screen: get(currentScreen),
        revision: match?._stateRevision || 0
      });
      if (snapshot) PeerSync.sendState(snapshot);
    }
    if (_onConnected) _onConnected();
  });

  conn.on("data", (raw) => {
    if (_conn !== conn || generation !== _connectionGeneration) return;
    try {
      const msg = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (!msg || !msg.t) return;

      switch (msg.t) {
        case "state":
          // The scorer sent a new state. Validate it and hand it to the
          // recorder controller (setupRecorderPeerHandlers), which builds the
          // synthetic _remoteControlled match and updates activeMatch. Never
          // put the raw snapshot into activeMatch directly: without the
          // _remoteControlled flag a stray persist() would overwrite this
          // phone's own saved match with the scorer's data.
          if (get(peerRole) === 'recorder') {
            if (_lastReceivedSnapshot && _lastReceivedSnapshot.id === msg.d.id && !isNewerPeerSnapshot(_lastReceivedSnapshot, msg.d)) break;
            const checked = validateMatchSnapshot(msg.d);
            if (!checked.ok) {
              console.warn("[PeerSync] Rejected invalid match state:", checked.error);
              break;
            }
            _lastReceivedSnapshot = { id: msg.d.id, revision: msg.d.revision, sentAt: msg.d.sentAt || msg.ts };
            if (_onState) _onState(checked.value);
          }
          break;
        case "anim":
          if (!_isValidAnimPayload(msg.d)) {
            console.warn("[PeerSync] Rejected malformed anim payload:", msg.d?.type);
            break;
          }
          if (_onAnim) _onAnim(msg.d);
          break;
        case "sfx":
          if (_onSfx && typeof msg.d?.fx === "string") _onSfx(msg.d);
          break;
        case "reccmd":
          if (_onRecCmd && ["start", "stop", "pause"].includes(msg.d?.action)) {
            // Acknowledge FIRST so the scorer stops retrying even if executing
            // the command takes a moment (or throws).
            if (msg.d.seq != null) {
              try { conn.send(JSON.stringify({ v: 1, t: "reccmdack", ts: Date.now(), d: { seq: msg.d.seq } })); } catch (_) {}
            }
            _onRecCmd(msg.d);
          }
          break;
        case "reccmdack":
          if (msg.d?.seq != null) _pendingRecCmds.delete(msg.d.seq);
          break;
        case "recstatus":
          if (_onRecStatus) _onRecStatus(msg.d);
          break;
        case "ping":
          if (_conn && _conn.open) {
            try { _conn.send(JSON.stringify({ v: 1, t: "pong", ts: Date.now() })); } catch (_) {}
          }
          break;
        case "pong":
          break;
        case "busy":
          // The scorer already has a recorder attached and refused us. Stop the
          // reconnect loop instead of hammering the signalling server.
          _reconnectAttempts = MAX_RECONNECT;
          _fireStatus("failed", "That match already has a recorder phone connected");
          break;
        default:
          console.warn("[PeerSync] Unknown message type:", msg.t);
      }
    } catch (err) {
      console.warn("[PeerSync] Bad message:", err);
    }
  });

  conn.on("close", () => {
    if (_conn !== conn || generation !== _connectionGeneration) return;
    _conn = null;
    console.warn("[PeerSync] Connection closed");
    _stopHeartbeat();
    _fireStatus("disconnected");
    peerConnected.set(false);
    if (_onDisconnected) _onDisconnected();
    
    if (get(peerRole) === "recorder" && get(peerRoomCode)) {
      _scheduleReconnect();
    }
  });

  conn.on("error", (err) => {
    if (_conn !== conn) return;
    console.error("[PeerSync] Connection error:", err);
    _fireStatus("error", String(err));
    if (get(peerRole) === "recorder") {
      try { conn.close(); } catch (_) {}
    }
  });
}

function _scheduleReconnect() {
  if (_reconnectTimer) return;
  if (_reconnectAttempts >= MAX_RECONNECT) {
    _fireStatus("failed", "Max reconnection attempts reached");
    return;
  }
  _reconnectAttempts++;
  const delay = Math.min(2000 * _reconnectAttempts, 10000);
  _fireStatus("reconnecting", `Attempt ${_reconnectAttempts}/${MAX_RECONNECT}`);
  
  _reconnectTimer = setTimeout(() => {
    _reconnectTimer = null;
    const room = get(peerRoomCode);
    if (!room) return;
    if (!_peer || _peer.destroyed) {
      console.log("[PeerSync] Peer instance missing/destroyed during reconnect. Re-initializing room join...");
      PeerSync.joinRoom(room).catch(err => {
        console.error("[PeerSync] Re-join room failed during reconnect:", err);
      });
    } else {
      console.log("[PeerSync] Reconnecting to:", _peerId(room));
      const conn = _peer.connect(_peerId(room), { reliable: true });
      _setupConnection(conn);
    }
  }, delay);
}

// Single entry point for states arriving over the Tier-2 Supabase fallback.
// CRITICAL: it must carry the same recorder-only guard as the WebRTC "state"
// branch. Without it, anyone who guesses the 6-digit room code can broadcast on
// realtime:match_<code> and overwrite the SCORER's own authoritative match.
function _applyFallbackState(payload) {
  if (!_onState || !payload) return;
  if (get(peerRole) !== 'recorder') return;
  const data = payload.data || payload;
  if (!isNewerPeerSnapshot(_lastReceivedSnapshot, data)) return;
  const checked = validateMatchSnapshot(data);
  if (!checked.ok) {
    console.warn("[PeerSync] Rejected invalid fallback state:", checked.error);
    return;
  }
  const ts = data.sentAt || (Date.now() + (++_monotonicFallback));
  _lastReceivedSnapshot = { id: data.id, revision: data.revision, sentAt: ts };
  _onState(checked.value);
}

// Match state is rigorously validated; anim payloads were not, and drawOverlay
// dereferences anim.stats.* / anim.teamA.players.* without guards. A malformed
// packet threw inside the rAF loop and killed the frame — spammed, it disables
// all broadcast graphics for the rest of the match.
function _isValidAnimPayload(a) {
  if (!a || typeof a !== "object" || Array.isArray(a)) return false;
  if (typeof a.type !== "string" || !a.type) return false;
  if (a.duration !== undefined && (typeof a.duration !== "number" || !Number.isFinite(a.duration))) return false;
  if (a.type === "WICKET_STATS" && (!a.stats || typeof a.stats !== "object")) return false;
  if (a.type === "PLAYER_LIST") {
    const okTeam = (t) => t && typeof t === "object" && typeof t.name === "string" && Array.isArray(t.players);
    if (!okTeam(a.teamA) || !okTeam(a.teamB)) return false;
  }
  return true;
}

function _send(msg) {
  if (!_conn || !_conn.open) return false;
  try {
    _conn.send(JSON.stringify(msg));
    return true;
  } catch (err) {
    console.warn("[PeerSync] Send failed:", err);
    return false;
  }
}

export const PeerSync = {
  get isConnected() { return _conn && _conn.open; },
  get isActive() { return !!_peer && !_peer.destroyed; },

  async createRoom(preferredCode = null, retryAttempt = 0) {
    await _ensurePeerJS();
    this.disconnect();
    // Becoming the scorer: shed any recorder-role handlers left over from an
    // earlier session on this device.
    if (retryAttempt === 0) this.clearRecorderHandlers();

    const requestedCode = /^\d{6}$/.test(String(preferredCode || "")) ? String(preferredCode) : null;
    const code = requestedCode || _genCode();
    peerRoomCode.set(code);
    peerRole.set("scorer");
    _fireStatus(retryAttempt > 0 ? "reconnecting" : "creating");

    return new Promise((resolve, reject) => {
      let regTimeout = null;
      try {
        const peer = new window.Peer(_peerId(code), { debug: 1, config: ICE_CONFIG });
        _peer = peer;

        regTimeout = setTimeout(() => {
          _fireStatus("error", "Couldn't reach connection server");
          try { peer.destroy(); } catch (_) {}
          if (_peer === peer) _peer = null;
          reject(new Error("Timeout"));
        }, 15000);

        peer.on("open", (id) => {
          if (_peer !== peer) return;
          if (regTimeout) clearTimeout(regTimeout);
          console.log("[PeerSync] Scorer registered as:", id);
          _fireStatus("waiting");
          try {
            initSupabaseFallbackChannel(code, _applyFallbackState);
          } catch (_) {}
          resolve(code);
        });

        peer.on("connection", (conn) => {
          if (_peer !== peer) return;
          // Room codes are only 6 digits. Silently adopting every inbound
          // connection let anyone who guessed the code kick the legitimate
          // recorder off mid-match. Hold the incumbent; handover goes through
          // the explicit "Change recorder phone" action (replaceRecorder).
          if (_conn && _conn.open) {
            console.warn("[PeerSync] Rejected a second recorder — session already in use:", conn.peer);
            if (conn.open) {
              try { conn.send(JSON.stringify({ v: 1, t: "busy" })); } catch (_) {}
            }
            try { conn.close(); } catch (_) {}
            _fireStatus("connected", "Rejected an extra recorder");
            return;
          }
          console.log("[PeerSync] Recorder connected:", conn.peer);
          _setupConnection(conn);
        });

        peer.on("error", (err) => {
          if (_peer !== peer) return;
          console.error("[PeerSync] Peer error:", err);
          if (err.type === "unavailable-id") {
            if (regTimeout) clearTimeout(regTimeout);
            try { peer.destroy(); } catch (_) {}
            if (_peer === peer) _peer = null;
            const delay = requestedCode ? Math.min(1000 * (retryAttempt + 1), 3000) : 0;
            // The PeerJS signalling server holds a released id for up to ~30s.
            // The old ceiling of 5 attempts gave up after ~12s, so a mid-match
            // scorer reconnect always dead-ended on "code is still busy".
            if (requestedCode && retryAttempt >= 14) {
              _fireStatus("error", "Saved recorder code is still busy");
              reject(new Error("Saved recorder code is still busy"));
              return;
            }
            setTimeout(() => {
              this.createRoom(requestedCode, retryAttempt + 1).then(resolve).catch(reject);
            }, delay);
          } else {
            if (regTimeout) clearTimeout(regTimeout);
            _fireStatus("error");
            reject(err);
          }
        });

        peer.on("disconnected", () => {
          if (_peer === peer && !peer.destroyed) try { peer.reconnect(); } catch (_) {}
        });
      } catch (err) {
        if (regTimeout) clearTimeout(regTimeout);
        reject(err);
      }
    });
  },

  restoreScorerSession(match) {
    const code = String(match?._peerRoomCode || "");
    if (match?._deviceRole !== "scorer" || !/^\d{6}$/.test(code)) return Promise.resolve(null);
    peerRole.set("scorer");
    peerRoomCode.set(code);
    _fireStatus("reconnecting");
    return this.createRoom(code);
  },

  reconnectScorer(match) {
    const code = String(match?._peerRoomCode || get(peerRoomCode) || "");
    if (!/^\d{6}$/.test(code)) return Promise.reject(new Error("No saved recorder code"));
    return this.createRoom(code);
  },

  replaceRecorder(match) {
    const code = String(match?._peerRoomCode || get(peerRoomCode) || "");
    if (!/^\d{6}$/.test(code)) return false;
    _connectionGeneration++;
    if (_conn) { try { _conn.close(); } catch (_) {} _conn = null; }
    peerConnected.set(false);
    peerRole.set("scorer");
    peerRoomCode.set(code);
    // The replacement recorder starts from scratch — a retained snapshot marker
    // would make its first packets look older than the departed phone's.
    _lastReceivedSnapshot = null;
    _fireStatus("waiting", "Waiting for replacement recorder");
    // If the peer was destroyed (e.g. after an explicit disconnect) nothing is
    // listening on this id any more — re-register so a new recorder can join.
    if (!_peer || _peer.destroyed) {
      this.createRoom(code).catch(err => {
        console.warn("[PeerSync] Could not re-open room for replacement recorder:", err);
        _fireStatus("error", "Could not reopen the room — check your connection");
      });
    }
    return true;
  },

  async joinRoom(code) {
    if (!code || code.length !== 6) throw new Error("Invalid room code");
    await _ensurePeerJS();
    this.disconnect();

    peerRoomCode.set(code);
    peerRole.set("recorder");
    _reconnectAttempts = 0;
    _lastReceivedSnapshot = null;
    _fireStatus("joining");

    return new Promise((resolve, reject) => {
      let timeout = null;
      try {
        _peer = new window.Peer(undefined, { debug: 1, config: ICE_CONFIG });

        timeout = setTimeout(() => {
          _fireStatus("error", "Connection timeout");
          this.disconnect();
          reject(new Error("Timeout"));
        }, 15000);

        _peer.on("open", () => {
          try {
            initSupabaseFallbackChannel(code, _applyFallbackState);
          } catch (_) {}
          const conn = _peer.connect(_peerId(code), { reliable: true });
          _setupConnection(conn);
          conn.on("open", () => {
            if (timeout) clearTimeout(timeout);
            resolve(code);
          });
        });

        _peer.on("call", (call) => {
          call.answer();
          call.on("stream", (remoteStream) => {
            if (_onAudioStream) _onAudioStream(remoteStream);
          });
        });

        _peer.on("disconnected", () => {
          if (_peer && !_peer.destroyed) try { _peer.reconnect(); } catch (_) {}
        });

        _peer.on("error", (err) => {
          if (err.type === "peer-unavailable") {
            if (timeout) clearTimeout(timeout);
            _fireStatus("error");
            reject(new Error("Room not found"));
          } else {
            if (timeout) clearTimeout(timeout);
            _fireStatus("error");
            reject(err);
          }
        });
      } catch (err) {
        if (timeout) clearTimeout(timeout);
        reject(err);
      }
    });
  },

  onState(fn) { _onState = fn; },
  onAnim(fn) { _onAnim = fn; },
  onSfx(fn) { _onSfx = fn; },
  onRecCmd(fn) { _onRecCmd = fn; },
  onRecStatus(fn) { _onRecStatus = fn; },
  onAudioStream(fn) { _onAudioStream = fn; },
  onConnected(fn) { _onConnected = fn; },
  onDisconnected(fn) { _onDisconnected = fn; },

  sendState(data) {
    return _send({ v: 1, t: "state", ts: Date.now(), d: data });
  },

  sendAnim(data) {
    return _send({ v: 1, t: "anim", ts: Date.now(), d: data });
  },

  sendSfx(sfxName) {
    return _send({ v: 1, t: "sfx", ts: Date.now(), d: { fx: sfxName } });
  },

  onRecCmdFailed(fn) { _onRecCmdFailed = fn; },

  sendRecCmd(action) {
    const seq = ++_recCmdSeq;
    const msg = { v: 1, t: "reccmd", ts: Date.now(), d: { action, seq } };
    // Queue BEFORE sending: if the DataChannel is momentarily closed the send
    // fails, but the retry loop still delivers it once the link is back —
    // previously the command was simply lost and the two phones desynced.
    _pendingRecCmds.set(seq, { msg, attempts: 0 });
    _ensureRecCmdRetryLoop();
    return _send(msg);
  },

  sendRecStatus(status) {
    const statusTs = Date.now();
    return _send({ v: 1, t: "recstatus", ts: statusTs, d: { ...(status || {}), statusTs } });
  },

  sendAudioStream(stream) {
    if (!_peer || _peer.destroyed || !_conn || !_conn.peer) return null;
    try {
      return _peer.call(_conn.peer, stream);
    } catch(err) {
      return null;
    }
  },

  disconnect() {
    _connectionGeneration++;
    _stopHeartbeat();
    _stopRecCmdRetries();
    if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
    if (_conn) { try { _conn.close(); } catch (_) {} _conn = null; }
    if (_peer) { try { _peer.destroy(); } catch (_) {} _peer = null; }
    
    try { closeSupabaseFallbackChannel(); } catch (_) {}
    peerRole.set(null);
    peerRoomCode.set(null);
    peerConnected.set(false);
    _reconnectAttempts = 0;
    _lastReceivedSnapshot = null;
    _fireStatus("disconnected");
  },

  // Drop the RECORDER-side handlers. They are module-level singletons, so a
  // phone used as Recorder and then switched to Scorer would otherwise keep
  // _onState wired and could apply remote states to its own live match.
  // Scorer-side handlers (onRecStatus/onConnected/onDisconnected) are owned by
  // Scoring.svelte's lifecycle and are deliberately left alone.
  clearRecorderHandlers() {
    _onState = null;
    _onAnim = null;
    _onSfx = null;
    _onRecCmd = null;
    _onAudioStream = null;
  },

  buildStateSnapshot(m, session = {}) {
    if (!m || !m.innings || m.innings.length === 0) return null;
    return {
      id: m.id,
      teamA: { name: m.teamA.name, players: m.teamA.players || [] },
      teamB: { name: m.teamB.name, players: m.teamB.players || [] },
      overs: m.overs,
      mode: m.mode,
      lms: !!m.lms,
      toss: m.toss || {},
      // Custom tournament rules must travel with the state, or the recorder's
      // burnt-in overlay computes extras / bowler quotas against T20 defaults.
      rules: m.rules || null,
      challenge: m.challenge || null,
      _introPlayedAt: m._introPlayedAt || null,
      result: m.result || null,
      revision: Number(session.revision ?? m._stateRevision) || 0,
      sentAt: Date.now(),
      // Ball result + AI commentary line. Without this the recorder phone — the
      // one that actually burns the overlay into the video — never received any
      // commentary, so it only ever appeared on the scorer's own screen.
      lastBall: session.lastBall || null,
      screen: session.screen || null,
      fullScreenBoard: session.board || null,
      innings: m.innings.map(i => ({
        battingTeam: i.battingTeam,
        batsmen: i.batsmen || [],
        bowlers: i.bowlers || [],
        totalRuns: i.totalRuns || 0,
        wickets: i.wickets || 0,
        balls: i.balls || 0,
        extras: i.extras || { wide: 0, nb: 0, b: 0, lb: 0 },
        ballLog: i.ballLog || [],
        currentStriker: i.currentStriker || null,
        currentNonStriker: i.currentNonStriker || null,
        currentBowler: i.currentBowler || null,
        freeHit: !!i.freeHit
      }))
    };
  }
};

if (typeof window !== "undefined") {
  window.PeerSync = PeerSync;
}
